import LMF from './language_model_factory'
import Lexeme from './lexeme.js'
import Lemma from './lemma.js'
import Logger from './logging/logger.js'

class Homonym {
  /**
   * Initializes a Homonym object.
   *
   * @param {Lexeme[]} lexemes - An array of Lexeme objects.
   * @param {string} form - the form which produces the homonyms
   */
  constructor (lexemes, form) {
    if (!lexemes || (Array.isArray(lexemes) && lexemes.length === 0)) {
      throw new Error('Lexemes data should not be empty.')
    }

    if (!Array.isArray(lexemes)) {
      throw new Error('Lexeme data should be provided in an array.')
    }

    for (const lexeme of lexemes) {
      if (!(lexeme instanceof Lexeme)) {
        throw new Error('All lexeme data should be of Lexeme object type.')
      }
    }

    /** @type {Lexeme[]} */
    this.lexemes = lexemes
    this.targetWord = form
  }

  /**
   * Creates a simple form of inflection with one lexeme and zero or more inflections
   * attached to it. The lexeme will have lemma whose `word` will be set to
   * a homonym's target word.
   *
   * @param {string} word - A word that will populate homonym's `targetWord` prop and lemma `word` one.
   * @param {symbol} languageID - A language identificator as defined in Constants.LANG_XXX.
   * @param {Inflection[]} inflections - Zero or more inflection objects that will be attached to the lexeme
   * @returns {Homonym} A newly created homonym object.
   */
  static createSimpleForm (word, languageID, inflections = []) {
    const lemma = new Lemma(word, languageID)
    const lexeme = new Lexeme(lemma, inflections)
    return new Homonym([lexeme], word)
  }

  /**
   * Checks if any of the lexemes of this homonym has short definitions stored.
   *
   * @returns {boolean} - true if any definitions are stored, false otherwise.
   */
  get hasShortDefs () {
    return Boolean(this.lexemes && this.lexemes.some(l => l.hasShortDefs))
  }

  /**
   * Checks if any of the lexemes of this homonym has full definitions stored.
   *
   * @returns {boolean} - true if any definitions are stored, false otherwise.
   */
  get hasFullDefs () {
    return Boolean(this.lexemes && this.lexemes.some(l => l.hasFullDefs))
  }

  static readObject (jsonObject) {
    let lexemes = [] // eslint-disable-line prefer-const
    if (jsonObject.lexemes) {
      for (const lexeme of jsonObject.lexemes) {
        lexemes.push(Lexeme.readObject(lexeme))
      }
    } else {
      const languageID = LMF.getLanguageIdFromCode(jsonObject.languageCode)
      lexemes = [new Lexeme(new Lemma(jsonObject.targetWord, languageID), [])]
    }
    const homonym = new Homonym(lexemes, jsonObject.form || jsonObject.targetWord)
    homonym.lemmasList = jsonObject.lemmasList
    return homonym
  }

  convertToJSONObject (addMeaning = false) {
    let resultHomonym = { lexemes: [], form: this.targetWord } // eslint-disable-line prefer-const
    for (const lexeme of this.lexemes) {
      resultHomonym.lexemes.push(lexeme.convertToJSONObject(addMeaning))
    }
    return resultHomonym
  }

  /**
   * Returns a language code of a homonym (ISO 639-3).
   * Homonym does not have a language property, only lemmas and inflections do. We assume that all lemmas
   * and inflections within the same homonym will have the same language, and we can determine a language
   * by using language property of the first lemma. We chan change this logic in the future if we'll need to.
   *
   * @returns {string} A language code, as defined in the `languages` object.
   */
  get language () {
    Logger.getInstance().warn('Please use languageID instead')
    return LMF.getLanguageCodeFromId(this.languageID)
  }

  /**
   * Returns a language ID of a homonym.
   * Homonym does not have a languageID property, only lemmas and inflections do. We assume that all lemmas
   * and inflections within the same homonym will have the same language, and we can determine a language
   * by using languageID property of the first lemma. We chan change this logic in the future if we'll need to.
   *
   * @returns {symbol} A language ID, as defined in the `LANG_` constants.
   */
  get languageID () {
    if (this.lexemes && this.lexemes[0] && this.lexemes[0].lemma && this.lexemes[0].lemma.languageID) {
      return this.lexemes[0].lemma.languageID
    } else {
      throw new Error('Homonym has not been initialized properly. Unable to obtain language ID information.')
    }
  }

  /**
   * Returns a list of all inflections within all lexemes of a homonym
   *
   * @returns {Inflection[]} An array of inflections
   */
  get inflections () {
    let inflections = []
    for (const lexeme of this.lexemes) {
      inflections = inflections.concat(lexeme.inflections)
    }
    return inflections
  }

  isDisambiguated () {
    return this.lexemes.filter(l => l.disambiguated).length > 0
  }

  /**
   * Disambiguate homymyn objects with another
   *
   * @param {Homonym} base the homonym to use to disambiguate
   * @param {Homonym[]} disambiguators the homonyms to use to disambiguate
   */
  static disambiguate (base, disambiguators) {
    if (disambiguators.length === 0) {
      // nothing left to disamibugate with
      return base
    }
    const disambiguator = disambiguators.shift()
    let matchedLexemes = [] // eslint-disable-line prefer-const
    let missedLexemes = [] // eslint-disable-line prefer-const
    let possibleLexemes = [] // eslint-disable-line prefer-const
    let unmatchedLexemes = [] // eslint-disable-line prefer-const
    // iterate through the lexemes in the disambiguator and try
    // to disambiguate the existing lexemes with each
    for (const otherLexeme of disambiguator.lexemes) {
      for (const lexeme of base.lexemes) {
        // Do not try to disambiguate lexemes that can't: it will erase a `disambiguated` flag
        const newLex = lexeme.canBeDisambiguatedWith(otherLexeme) ? Lexeme.disambiguateInflections(lexeme, otherLexeme) : lexeme

        if (lexeme.isFullHomonym(otherLexeme, { normalize: true })) {
          if (newLex.getSelectedInflection() !== null) {
            // If lexeme is a full homonym with a disambiguator and had a matching
            // inflection, it is a full match
            newLex.setDisambiguation(otherLexeme)
            matchedLexemes.push(newLex)
          } else {
            // If lexeme is a full homonym with a disambiguator but didn't have a
            // matching inflection, it may or may not be a full match depending
            // upon whether any other lexeme was a more complete match
            possibleLexemes.push(newLex)
          }
        } else {
          unmatchedLexemes.push(newLex)
        }
      }
      if (matchedLexemes.length === 0) {
        if (possibleLexemes.length > 0) {
          // we didn't have a better match so mark as disamibugated
          // and add in the disambiguator's inflections
          for (const lexeme of possibleLexemes) {
            lexeme.setDisambiguation(otherLexeme)
            // we have to add in the disamibugators inflections
            for (const infl of disambiguator.inflections) {
              lexeme.addInflection(infl)
              lexeme.setSelectedInflection(infl)
            }
          }
        } else {
          // if we couldn't find a matching lexeme, add the disambigutor's lexemes
          // to the list of lexemes for the new Homonym
          otherLexeme.setDisambiguation()
          for (const infl of otherLexeme.inflections) {
            otherLexeme.setSelectedInflection(infl)
          }
          missedLexemes.push(otherLexeme)
        }
      }
    }
    // create a new homonym with the disamibugated lexemes
    const newHom = new Homonym([...missedLexemes, ...matchedLexemes, ...possibleLexemes, ...unmatchedLexemes], base.targetWord)
    return Homonym.disambiguate(newHom, disambiguators)
  }
}
export default Homonym
